import api from "../../services/api";

const CACHE_PREFIX = "syncs-whatsapp-media-v1";
const MAX_CACHE_BYTES = 250 * 1024 * 1024;
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const inFlight = new Map();

function safeId(value) {
  return (
    String(value || "")
      .replace(/[^a-zA-Z0-9_-]/g, "")
      .slice(0, 80) || "unknown"
  );
}

function cacheName(empresaId, userId) {
  return `${CACHE_PREFIX}-${safeId(empresaId)}-${safeId(userId)}`;
}

function requestKey(messageId, empresaId, userId) {
  const base =
    typeof window === "undefined"
      ? "https://local.invalid"
      : window.location.origin;
  return new Request(
    `${base}/__syncs-private-whatsapp-media/${safeId(empresaId)}/${safeId(userId)}/${safeId(messageId)}`,
  );
}

async function enforceLimit(cache) {
  const entries = [];
  let total = 0;
  for (const request of await cache.keys()) {
    const response = await cache.match(request);
    if (!response) continue;
    const size = Number(response.headers.get("X-Syncs-Media-Size") || 0);
    const cachedAt = Number(response.headers.get("X-Syncs-Cached-At") || 0);
    if (!Number.isFinite(size) || size < 0) continue;
    total += size;
    entries.push({ request, size, cachedAt });
  }
  entries.sort((left, right) => left.cachedAt - right.cachedAt);
  for (const entry of entries) {
    if (total <= MAX_CACHE_BYTES) break;
    await cache.delete(entry.request);
    total -= entry.size;
  }
}

async function readCache(messageId, empresaId, userId) {
  if (typeof caches === "undefined") return null;
  const cache = await caches.open(cacheName(empresaId, userId));
  const key = requestKey(messageId, empresaId, userId);
  const response = await cache.match(key);
  if (!response) return null;
  const cachedAt = Number(response.headers.get("X-Syncs-Cached-At") || 0);
  if (!cachedAt || Date.now() - cachedAt > MAX_AGE_MS) {
    await cache.delete(key);
    return null;
  }
  return response.blob();
}

async function writeCache(messageId, empresaId, userId, blob) {
  if (typeof caches === "undefined" || !blob || blob.size > MAX_CACHE_BYTES)
    return;
  try {
    const cache = await caches.open(cacheName(empresaId, userId));
    await cache.put(
      requestKey(messageId, empresaId, userId),
      new Response(blob, {
        headers: {
          "Content-Type": blob.type || "application/octet-stream",
          "X-Syncs-Media-Size": String(blob.size),
          "X-Syncs-Cached-At": String(Date.now()),
        },
      }),
    );
    await enforceLimit(cache);
  } catch {
    // Quota ou modo privado nunca devem impedir a visualizacao obtida da API.
  }
}

export async function getMetaMediaBlob(messageId, empresaId, userId) {
  const scope = `${safeId(empresaId)}:${safeId(userId)}:${safeId(messageId)}`;
  if (inFlight.has(scope)) return inFlight.get(scope);
  const task = (async () => {
    const cached = await readCache(messageId, empresaId, userId).catch(
      () => null,
    );
    if (cached) return cached;
    const response = await api.get(
      `/whatsapp-meta/messages/${messageId}/media`,
      { responseType: "blob" },
    );
    const blob = response.data;
    await writeCache(messageId, empresaId, userId, blob);
    return blob;
  })();
  inFlight.set(scope, task);
  try {
    return await task;
  } finally {
    inFlight.delete(scope);
  }
}

export async function getMetaMediaBrowserCacheStatus(
  messageId,
  empresaId,
  userId,
) {
  if (typeof caches === "undefined") {
    return { supported: false, cached: false };
  }
  try {
    const name = cacheName(empresaId, userId);
    const names = await caches.keys();
    if (!names.includes(name)) return { supported: true, cached: false };
    const cache = await caches.open(name);
    const key = requestKey(messageId, empresaId, userId);
    const response = await cache.match(key);
    if (!response) return { supported: true, cached: false };
    const cachedAt = Number(response.headers.get("X-Syncs-Cached-At") || 0);
    if (!cachedAt || Date.now() - cachedAt > MAX_AGE_MS) {
      await cache.delete(key);
      return { supported: true, cached: false };
    }
    return {
      supported: true,
      cached: true,
      cached_at: new Date(cachedAt).toISOString(),
      size_bytes: Number(response.headers.get("X-Syncs-Media-Size") || 0),
    };
  } catch {
    return { supported: false, cached: false };
  }
}

export async function clearWhatsappMediaBrowserCaches() {
  inFlight.clear();
  if (typeof caches === "undefined") return;
  const names = await caches.keys();
  await Promise.all(
    names
      .filter((name) => name.startsWith(CACHE_PREFIX))
      .map((name) => caches.delete(name)),
  );
}
